import { useEffect, useRef, useState } from 'react'

interface Route {
  path: string
  component: React.ComponentType<any>
  title?: string
}

interface RouterProps {
  routes: Route[]
}

function matchRoute(
  routePath: string,
  currentPath: string,
): { match: boolean; params: Record<string, string> } {
  const routeParts = routePath.split('/')
  const currentParts = currentPath.split('/')

  if (routeParts.length !== currentParts.length) {
    return { match: false, params: {} }
  }

  const params: Record<string, string> = {}

  for (let i = 0; i < routeParts.length; i++) {
    const routePart = routeParts[i]
    const currentPart = currentParts[i]

    if (routePart.startsWith(':')) {
      const paramName = routePart.slice(1)
      params[paramName] = currentPart
    }
    else if (routePart !== currentPart) {
      return { match: false, params: {} }
    }
  }

  return { match: true, params }
}

let navigateFunction: ((path: string) => void) | null = null

export function navigate(path: string) {
  const params = new URLSearchParams(window.location.search)
  const hasNoAccount = params.has('noaccount')

  const getPathWithNoAccount = (url: string) => {
    if (!hasNoAccount) return url

    // Check if the path already contains noaccount
    const urlParams = new URLSearchParams(url.split('?')[1] || '')
    if (urlParams.has('noaccount')) return url

    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}noaccount`
  }

  const finalPath = getPathWithNoAccount(path)

  if (navigateFunction) {
    navigateFunction(finalPath)
  }
  else {
    window.history.pushState({}, '', finalPath)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }
}

export function Router({ routes }: RouterProps) {
  const [currentPath, setCurrentPath] = useState(window.location.pathname)

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const navigateRef = useRef<(path: string) => void>(null)

  navigateRef.current = (path: string) => {
    window.history.pushState({}, '', path)
    const pathname = path.split('?')[0]
    setCurrentPath(pathname)
  }

  useEffect(() => {
    navigateFunction = (path: string) => {
      if (navigateRef.current) {
        navigateRef.current(path)
      }
    }
    return () => {
      navigateFunction = null
    }
  }, [])

  let matchedRoute: Route | null = null
  let params: Record<string, string> = {}
  const pathname = currentPath.split('?')[0]

  for (const route of routes) {
    const result = matchRoute(route.path, pathname)
    if (result.match) {
      matchedRoute = route
      params = result.params
      break
    }
  }

  if (!matchedRoute) {
    matchedRoute = routes[0]
  }

  if (!matchedRoute) {
    return <div>404 Not Found</div>
  }

  useEffect(() => {
    if (matchedRoute?.title) {
      document.title = matchedRoute.title
    }
  }, [matchedRoute])

  const Component = matchedRoute.component
  return <Component {...params} />
}

export function Link({
  href,
  children,
  onClick,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const params = new URLSearchParams(window.location.search)
  const hasNoAccount = params.has('noaccount')

  const getHrefWithNoAccount = (url: string | undefined) => {
    if (!url || !hasNoAccount) return url

    // Check if the url already contains noaccount
    const urlParams = new URLSearchParams(url.split('?')[1] || '')
    if (urlParams.has('noaccount')) return url

    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}noaccount`
  }

  const finalHref = getHrefWithNoAccount(href)

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (props.target) return
    e.preventDefault()
    if (finalHref) {
      navigate(finalHref)
    }
    onClick?.(e)
  }

  return (
    <a href={finalHref} onClick={handleClick} {...props} style={{ userSelect: 'none' }}>
      {children}
    </a>
  )
}
